import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Syringe, AlertTriangle, ShieldCheck, CalendarClock } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

// The injectables record — the last part of the blueprint that had no screen at all.
//
// Ordered the way the treatment actually happens, not the way the table is laid out: what was
// given, from which batch, where it went, what happened, what the patient was told. Traceability
// is first because that is what a product recall or a claim asks for, and "we think it was the
// usual one" is not an answer.

const blank = () => ({
  consent_id: '', performed_at: '', performed_by: '',
  product_trade_name: '', product_generic_name: '', manufacturer: '',
  batch_lot_number: '', expiry_date: '', strength: '', total_dose: '',
  needle_or_cannula: '', reconstitution: '', injection_map: '',
  immediate_findings: '', adverse_events: '', aftercare_given: '', review_due_on: '',
})

function Field({ label, hint, children, required }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-brand-muted">
        {label}{required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-0.5 text-[11px] text-brand-muted">{hint}</p>}
    </div>
  )
}

const input = 'w-full rounded-lg border border-brand-border p-2 text-sm focus:border-brand-primary focus:outline-none'

export default function AestheticRecords({ patient = {}, records = [], consents = [] }) {
  const [v, setV] = useState(blank)
  const [saving, setSaving] = useState(false)
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }))

  const traceable = v.batch_lot_number.trim() && v.expiry_date
  const submit = () => {
    setSaving(true)
    router.post(`/patients/${patient.id}/aesthetic-records`, { aesthetic_treatment_record: v }, {
      preserveScroll: true,
      onFinish: () => setSaving(false),
      onSuccess: () => setV(blank()),
    })
  }

  return (
    <DashboardLayout>
      <Link href={`/patients/${patient.id}/file`} className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> {patient.name} — digital file
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Syringe size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Aesthetic treatments — {patient.name}</h1>
          <p className="text-sm text-brand-muted">Product, batch and where it went. This is the record a recall or a claim reads.</p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <div className="rounded-xl border border-brand-border bg-white p-5">
          <h2 className="mb-3 text-sm font-semibold text-brand-ink">Record a treatment</h2>

          <div className={cn('mb-4 rounded-lg border p-3', traceable ? 'border-emerald-300 bg-emerald-50/50' : 'border-amber-300 bg-amber-50/60')}>
            <p className="mb-2 text-xs font-semibold text-brand-ink">
              {traceable ? 'Traceable ✓' : 'Traceability — fill these first'}
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Product (trade name)" required>
                <input className={input} value={v.product_trade_name} onChange={(e) => set('product_trade_name', e.target.value)} placeholder="e.g. Botox, Juvéderm Ultra" />
              </Field>
              <Field label="Generic name">
                <input className={input} value={v.product_generic_name} onChange={(e) => set('product_generic_name', e.target.value)} placeholder="botulinum toxin type A / HA filler" />
              </Field>
              <Field label="Manufacturer">
                <input className={input} value={v.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} />
              </Field>
              <Field label="Batch / lot number" required hint="What a recall is issued against.">
                <input className={input} value={v.batch_lot_number} onChange={(e) => set('batch_lot_number', e.target.value)} />
              </Field>
              <Field label="Expiry date" required>
                <input type="date" className={input} value={v.expiry_date} onChange={(e) => set('expiry_date', e.target.value)} />
              </Field>
              <Field label="Strength / concentration">
                <input className={input} value={v.strength} onChange={(e) => set('strength', e.target.value)} placeholder="e.g. 100U vial, 24mg/ml" />
              </Field>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Consent this treatment relies on" hint="A signed aesthetic or treatment consent.">
              <select className={input} value={v.consent_id} onChange={(e) => set('consent_id', e.target.value)}>
                <option value="">— none linked —</option>
                {consents.map((c) => <option key={c.id} value={c.id}>{c.label} · {c.signed_at}</option>)}
              </select>
            </Field>
            <Field label="Total dose given" required hint="Units or ml for the whole session.">
              <input className={input} value={v.total_dose} onChange={(e) => set('total_dose', e.target.value)} placeholder="e.g. 44U / 1.2ml" />
            </Field>
          </div>

          <div className="mt-3">
            <Field label="Dose per site / injection map" required
                   hint="Site by site, side-specific — this is the field a complication is investigated from.">
              <textarea rows={3} className={input} value={v.injection_map} onChange={(e) => set('injection_map', e.target.value)}
                placeholder="Glabella 20U (5 points x4U) · R frontalis 8U · L frontalis 8U · R crow's feet 4U · L crow's feet 4U" />
            </Field>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Needle / cannula">
              <input className={input} value={v.needle_or_cannula} onChange={(e) => set('needle_or_cannula', e.target.value)} placeholder="30G needle / 25G cannula" />
            </Field>
            <Field label="Reconstitution (toxin)" hint="Diluent, volume, final concentration, by whom.">
              <input className={input} value={v.reconstitution} onChange={(e) => set('reconstitution', e.target.value)} placeholder="2.5ml 0.9% saline → 4U/0.1ml, by CLR" />
            </Field>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="How the patient tolerated it">
              <textarea rows={2} className={input} value={v.immediate_findings} onChange={(e) => set('immediate_findings', e.target.value)} />
            </Field>
            <Field label="Adverse events" hint="Bruising, vasovagal, asymmetry, blanching. Blank means none observed.">
              <textarea rows={2} className={input} value={v.adverse_events} onChange={(e) => set('adverse_events', e.target.value)} />
            </Field>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <Field label="Aftercare given" hint="What they were told, including warning signs.">
              <textarea rows={2} className={input} value={v.aftercare_given} onChange={(e) => set('aftercare_given', e.target.value)} />
            </Field>
            <Field label="Review due">
              <input type="date" className={input} value={v.review_due_on} onChange={(e) => set('review_due_on', e.target.value)} />
            </Field>
          </div>

          <button onClick={submit} disabled={saving}
            className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:brightness-95 disabled:opacity-50">
            <Syringe size={14} /> {saving ? 'Saving…' : 'Record treatment'}
          </button>
        </div>

        <div>
          <h2 className="mb-2 text-sm font-semibold text-brand-ink">On the record ({records.length})</h2>
          <div className="space-y-3">
            {records.length === 0 && (
              <p className="rounded-xl border border-brand-border bg-white p-4 text-sm text-brand-muted">
                Nothing recorded yet for this patient.
              </p>
            )}
            {records.map((r) => (
              <div key={r.id} className="rounded-xl border border-brand-border bg-white p-4">
                <div className="mb-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-brand-ink">{r.product_trade_name || 'Product not named'}</span>
                  {r.traceable ? (
                    <span className="inline-flex items-center gap-1 rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                      <ShieldCheck size={10} /> Traceable
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-semibold text-amber-800">
                      <AlertTriangle size={10} /> No batch/expiry
                    </span>
                  )}
                  {r.expired_at_use && (
                    <span className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[11px] font-bold text-red-700">
                      ⚠ PRODUCT WAS PAST EXPIRY
                    </span>
                  )}
                  <span className="ml-auto text-xs text-brand-muted">{r.performed_at}</span>
                </div>
                <dl className="grid grid-cols-[110px_1fr] gap-x-2 gap-y-0.5 text-xs">
                  {r.batch_lot_number && (<><dt className="text-brand-muted">Batch</dt><dd className="font-mono text-brand-ink">{r.batch_lot_number}{r.expiry_date ? ` · exp ${r.expiry_date}` : ''}</dd></>)}
                  {r.total_dose && (<><dt className="text-brand-muted">Dose</dt><dd className="text-brand-ink">{r.total_dose}</dd></>)}
                  {r.injection_map && (<><dt className="text-brand-muted">Sites</dt><dd className="whitespace-pre-wrap text-brand-ink">{r.injection_map}</dd></>)}
                  {r.reconstitution && (<><dt className="text-brand-muted">Reconstituted</dt><dd className="text-brand-ink">{r.reconstitution}</dd></>)}
                  {r.adverse_events && (<><dt className="text-brand-muted">Adverse</dt><dd className="text-red-700">{r.adverse_events}</dd></>)}
                  {r.performed_by && (<><dt className="text-brand-muted">By</dt><dd className="text-brand-ink">{r.performed_by}</dd></>)}
                </dl>
                {r.review_due_on && (
                  <p className="mt-2 flex items-center gap-1 text-[11px] text-brand-muted">
                    <CalendarClock size={11} /> Review due {r.review_due_on}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
